import {
  selectBundleSearchFocusedPosition,
  selectBundleSearchHighlightPageId,
  selectBundleSearchText,
  selectMedicalMatchHighlightPageId,
  selectMedicalMatchHighlightTexts,
  useAppSelector,
} from '@/store';
import { RefObject, useCallback, useEffect, useState } from 'react';

interface UsePdfPageHighlightTextProps {
  pageInputRef: RefObject<HTMLDivElement | null>;
  pageId: string;
  height: number;
  width: number;
}

const WAIT_WHILE_DOM_UPDATED_MS = 50;

export const usePdfPageHighlightText = ({ pageInputRef, pageId, height, width }: UsePdfPageHighlightTextProps) => {
  const [rerenderTrigger, setRerenderTrigger] = useState<number>(Date.now());
  const [isTextLayerLoaded, setIsTextLayerLoaded] = useState<boolean>(false);
  const bundleSearchHighlightPageId = useAppSelector(selectBundleSearchHighlightPageId);
  const bundleSearchFocusedPosition = useAppSelector(selectBundleSearchFocusedPosition);
  const bundleSearchText = useAppSelector(selectBundleSearchText);
  const medicalMatchHighlightPageId = useAppSelector(selectMedicalMatchHighlightPageId);
  const medicalMatchHighlightTexts = useAppSelector(selectMedicalMatchHighlightTexts);

  useEffect(() => {
    if (
      pageInputRef.current &&
      bundleSearchText?.length &&
      bundleSearchHighlightPageId &&
      bundleSearchFocusedPosition &&
      pageId === bundleSearchHighlightPageId
    ) {
      const focusedElement = bundleSearchFocusedPosition - 1;
      const textLayer = pageInputRef.current.querySelector('.react-pdf__Page__textContent.textLayer');

      if (textLayer) {
        const treeWalker = document.createTreeWalker(textLayer, NodeFilter.SHOW_ALL);
        const allTextNodes: Text[] = [];

        while (treeWalker.nextNode()) {
          if (treeWalker.currentNode.nodeType === Node.TEXT_NODE) {
            allTextNodes.push(treeWalker.currentNode as Text);
          }
        }

        const queryText = bundleSearchText.trim().toLowerCase();
        if (!queryText.length) return;

        let fullText = '';
        const nodeMap: { node: Text; start: number; end: number }[] = [];

        for (let i = 0; i < allTextNodes.length; i++) {
          const node = allTextNodes[i];
          const nodeText = node.textContent || '';
          const trimmedNodeText = nodeText.replace(/\s+/g, ' '); // normalize internal spaces
          const start = fullText.length;
          fullText += trimmedNodeText;

          nodeMap.push({ node, start, end: fullText.length });

          const isLast = i === allTextNodes.length - 1;
          const nextNode = allTextNodes[i + 1];
          const currentEndsWithSpace = /\s$/.test(trimmedNodeText);
          const nextStartsWithSpace = nextNode ? /^\s/.test(nextNode.textContent || '') : false;

          if (!isLast && !currentEndsWithSpace && !nextStartsWithSpace) {
            fullText += ' ';
          }
        }

        const matches: { start: number; end: number }[] = [];
        const wordStartRegex = /\b\w/g;

        for (let i = 0; i < fullText.length; i++) {
          if (wordStartRegex.test(fullText[i])) {
            const slice = fullText.slice(i, i + queryText.length).toLowerCase();
            if (slice === queryText) {
              matches.push({ start: i, end: i + queryText.length });
            }
          }
        }

        const matchesGroups: { node: Text; start: number; end: number }[][] = [];
        let currentGroup: { node: Text; start: number; end: number }[] = [];

        matches.forEach((match) => {
          let remaining = match.end - match.start;
          let cursor = match.start;

          for (const { node, start, end } of nodeMap) {
            if (cursor >= end) continue;
            if (cursor < start && match.end <= start) break;

            const nodeRelativeStart = Math.max(cursor - start, 0);
            const nodeRelativeEnd = Math.min(end, match.end) - start;

            currentGroup.push({
              node,
              start: nodeRelativeStart,
              end: nodeRelativeEnd,
            });

            const consumed = nodeRelativeEnd - nodeRelativeStart;
            remaining -= consumed;
            cursor += consumed;

            if (remaining <= 0) break;
          }

          if (remaining <= 0) {
            matchesGroups.push(currentGroup);
            currentGroup = [];
          }
        });

        const createRange = ({ node, start, end }: { node: Text; start: number; end: number }): Range => {
          const range = document.createRange();
          range.setStart(node, start);
          range.setEnd(node, end);
          return range;
        };

        const searchResultsHighlight = new Highlight(
          ...matchesGroups
            .filter((_, index) => index !== focusedElement)
            .map((group) => [...group.map(createRange)])
            .flat(),
        );

        const focusedSearchResultsHighlight = matchesGroups[focusedElement]
          ? new Highlight(...matchesGroups[focusedElement].map(createRange))
          : new Highlight();

        CSS.highlights.set('search-results', searchResultsHighlight);
        CSS.highlights.set('focused-search-result', focusedSearchResultsHighlight);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerenderTrigger, isTextLayerLoaded, bundleSearchHighlightPageId, bundleSearchFocusedPosition, bundleSearchText]);

  // Medical Match snippet highlight effect
  useEffect(() => {
    // Clear previous medical match highlights if this page is not the target
    if (pageId !== medicalMatchHighlightPageId) {
      return;
    }

    if (
      pageInputRef.current &&
      medicalMatchHighlightTexts.length > 0 &&
      medicalMatchHighlightPageId &&
      pageId === medicalMatchHighlightPageId
    ) {
      const textLayer = pageInputRef.current.querySelector('.react-pdf__Page__textContent.textLayer');

      if (textLayer) {
        const treeWalker = document.createTreeWalker(textLayer, NodeFilter.SHOW_ALL);
        const allTextNodes: Text[] = [];

        while (treeWalker.nextNode()) {
          if (treeWalker.currentNode.nodeType === Node.TEXT_NODE) {
            allTextNodes.push(treeWalker.currentNode as Text);
          }
        }

        let fullText = '';
        const nodeMap: { node: Text; start: number; end: number }[] = [];

        for (let i = 0; i < allTextNodes.length; i++) {
          const node = allTextNodes[i];
          const nodeText = node.textContent || '';
          const trimmedNodeText = nodeText.replace(/\s+/g, ' ');
          const start = fullText.length;
          fullText += trimmedNodeText;

          nodeMap.push({ node, start, end: fullText.length });

          const isLast = i === allTextNodes.length - 1;
          const nextNode = allTextNodes[i + 1];
          const currentEndsWithSpace = /\s$/.test(trimmedNodeText);
          const nextStartsWithSpace = nextNode ? /^\s/.test(nextNode.textContent || '') : false;

          if (!isLast && !currentEndsWithSpace && !nextStartsWithSpace) {
            fullText += ' ';
          }
        }

        const fullTextLower = fullText.toLowerCase();
        const allMatches: { start: number; end: number }[] = [];

        for (const snippetText of medicalMatchHighlightTexts) {
          // Normalize snippet: replace newlines and collapse all whitespace to single space,
          // matching how the PDF text layer text is built (each node's whitespace collapsed,
          // nodes joined with single spaces)
          const queryText = snippetText
            .replace(/[\r\n]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
          if (!queryText.length) continue;

          // Try full snippet match
          let searchFrom = 0;
          let foundFullMatch = false;
          while (searchFrom < fullTextLower.length) {
            const idx = fullTextLower.indexOf(queryText, searchFrom);
            if (idx === -1) break;
            foundFullMatch = true;
            allMatches.push({ start: idx, end: idx + queryText.length });
            searchFrom = idx + 1;
          }

          // If full snippet didn't match, try splitting into shorter phrases and matching individually
          if (!foundFullMatch) {
            const fragments = queryText
              .split(/[|;]/)
              .map((f) => f.trim())
              .filter((f) => f.length > 10);
            for (const fragment of fragments) {
              let fragSearchFrom = 0;
              while (fragSearchFrom < fullTextLower.length) {
                const idx = fullTextLower.indexOf(fragment, fragSearchFrom);
                if (idx === -1) break;
                allMatches.push({ start: idx, end: idx + fragment.length });
                fragSearchFrom = idx + 1;
              }
            }
          }
        }

        // Cleared rather than left alone, or jumping here from an entry that did
        // match on this page would keep that entry's highlight up.
        if (allMatches.length === 0) {
          CSS.highlights.delete('medical-match-highlight');
          return;
        }

        const createRange = ({ node, start, end }: { node: Text; start: number; end: number }): Range => {
          const range = document.createRange();
          range.setStart(node, start);
          range.setEnd(node, end);
          return range;
        };

        const matchRanges: Range[] = [];

        for (const match of allMatches) {
          let remaining = match.end - match.start;
          let cursor = match.start;

          for (const { node, start, end } of nodeMap) {
            if (cursor >= end) continue;
            if (cursor < start && match.end <= start) break;

            const nodeRelativeStart = Math.max(cursor - start, 0);
            const nodeRelativeEnd = Math.min(end, match.end) - start;

            matchRanges.push(createRange({ node, start: nodeRelativeStart, end: nodeRelativeEnd }));

            const consumed = nodeRelativeEnd - nodeRelativeStart;
            remaining -= consumed;
            cursor += consumed;

            if (remaining <= 0) break;
          }
        }

        if (matchRanges.length > 0) {
          const medicalMatchHighlight = new Highlight(...matchRanges);
          CSS.highlights.set('medical-match-highlight', medicalMatchHighlight);
        } else {
          CSS.highlights.delete('medical-match-highlight');
        }
      }
    } else {
      CSS.highlights.delete('medical-match-highlight');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rerenderTrigger, isTextLayerLoaded, medicalMatchHighlightPageId, medicalMatchHighlightTexts]);

  useEffect(() => {
    setIsTextLayerLoaded(false);
    setRerenderTrigger(Date.now());
  }, [height, width]);

  const onRenderTextLayerSuccess = useCallback(() => {
    setTimeout(() => {
      setIsTextLayerLoaded(true);
      setRerenderTrigger(Date.now());
    }, WAIT_WHILE_DOM_UPDATED_MS);
  }, []);

  return { onRenderTextLayerSuccess };
};
